import { supabase } from '../supabaseClient';
import { resolveStorageUrl } from '../utils/storageUrl';

export const speakersService = {
  async uploadSpeakerImage(file, editionYear) {
    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `speaker-${timestamp}.${fileExt}`;
      const filePath = `${editionYear}/speakers/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('speaker-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async uploadCompanyLogo(file, editionYear) {
    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `company-${timestamp}.${fileExt}`;
      const filePath = `${editionYear}/companies/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('speaker-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async uploadLaboratoryPDF(file, editionYear) {
    try {
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `lab-${timestamp}.${fileExt}`;
      const filePath = `${editionYear}/laboratories/${fileName}`;

      const { data, error } = await supabase.storage
        .from('speaker-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async deleteSpeakerFile(filePath) {
    try {
      if (!filePath) return;

      const { error } = await supabase.storage
        .from('speaker-images')
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async getPersons(query) {
    try {
      let request = supabase.from('persons').select('*');
      if (query) {
        request = request.ilike('name', `%${query}%`);
      }
      const { data, error } = await request.limit(10);
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async getSpeakersData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get page data
      const { data: pageData, error: pageError } = await supabase
        .from('speakers_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (pageError && pageError.code !== 'PGRST116') {
      }

      // Get speakers
      const { data: speakers } = await supabase
        .from('speakers')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      // Get speaker-cadre relationships with ordering
      const { data: speakerCadres } = await supabase
        .from('speaker_cadres')
        .select(`
          speaker_id,
          display_order,
          cadre_conference!cadre_id (id, title, display_order)
        `)
        .order('display_order', { ascending: true });

      const { data: laboratories } = await supabase
        .from('speaker_laboratories')
        .select('*')
        .eq('edition_id', edition.id)
        .order('id', { ascending: true });

      const labIds = (laboratories || []).map(lab => lab.id);
      let laboratoryMembers = [];

      if (labIds.length > 0) {
        const { data: membersData } = await supabase
          .from('speaker_laboratory_members')
          .select('*')
          .in('laboratory_id', labIds);

        laboratoryMembers = membersData || [];
      }

      const labMap = new Map((laboratories || []).map(lab => [lab.id, lab]));
      const roleLabels = {
        president: 'Président',
        international_expert: 'Expert international',
        academic_expert: 'Expert académique',
        security_expert: 'Expert sécurité'
      };

      const enrichSpeaker = (speaker) => {
        const cadres = (speakerCadres || [])
          .filter(sc => sc.speaker_id === speaker.id)
          .map(sc => ({
            ...sc.cadre_conference,
            display_order: sc.cadre_conference?.display_order || 999,
            speaker_order: sc.display_order || 999
          }))
          .sort((a, b) => (a.display_order || 999) - (b.display_order || 999));

        const assignments = (laboratoryMembers || [])
          .filter(member => String(member.speaker_id) === String(speaker.id))
          .map(member => ({
            ...member,
            lab: labMap.get(member.laboratory_id) || null,
            roleLabel: roleLabels[member.role] || member.role
          }))
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

        const primaryAssignment = assignments[0] || null;
        const lab = primaryAssignment?.lab || null;

        return {
          id: speaker.id,
          name: speaker.name,
          title: speaker.title,
          organization: speaker.organization,
          image: resolveStorageUrl(speaker.image_url, 'speaker-images', speaker.image_path),
          imagePath: speaker.image_path,
          companyLogo: resolveStorageUrl(speaker.company_logo_url, 'speaker-images', speaker.company_logo_path),
          companyLogoPath: speaker.company_logo_path,
          country: speaker.country,
          flagUrl: resolveStorageUrl(speaker.flag_url, 'speaker-images', speaker.flag_path),
          flagPath: speaker.flag_path,
          displayOrder: speaker.display_order,
          mainCategory: speaker.main_category,
          cadres: cadres,
          cadreIds: cadres.map(c => c.id),
          cadreTitle: cadres.map(c => c.title).join(', '),
          laboratory: lab,
          laboratoryId: lab?.id || null,
          laboratoryTitle: lab?.title || '',
          laboratorySubtitle: lab?.subtitle || '',
          laboratoryDescription: lab?.description || '',
          laboratoryObjectives: lab?.objectives || '',
          laboratoryThemes: lab?.themes || '',
          laboratoryValue: lab?.value || '',
          role: primaryAssignment?.role || null,
          roleLabel: primaryAssignment?.roleLabel || null,
          displayOrderInLab: primaryAssignment?.display_order ?? speaker.display_order,
          laboratoryAssignments: assignments
        };
      };

      const regularSpeakers = (speakers || [])
        .filter(speaker => speaker.type === 'regular')
        .map(enrichSpeaker);

      const onlineSpeakers = (speakers || [])
        .filter(speaker => speaker.type === 'online')
        .map(enrichSpeaker);

      return {
        speakersHeader: {
          title: pageData?.speakers_header_title || 'Nos Intervenants'
        },
        speakersIntro: {
          text: pageData?.speakers_intro_text || ''
        },
        speakersSection: {
          title: pageData?.speakers_section_title || 'Intervenants Confirmés'
        },
        onlineSpeakersHeader: {
          title: pageData?.online_speakers_header_title || 'Intervenants En Ligne'
        },
        onlineSpeakersIntro: {
          text: pageData?.online_speakers_intro_text || ''
        },
        onlineSpeakersSection: {
          title: pageData?.online_speakers_section_title || 'Conférenciers Online'
        },
        displayMode: pageData?.display_mode || 'grouped',
        categoryInternationalOrder: pageData?.category_international_order ?? 1,
        categoryMarocainOrder: pageData?.category_marocain_order ?? 2,
        speakers: regularSpeakers,
        onlineSpeakers: onlineSpeakers,
        laboratories: (laboratories || []).map(lab => ({
          id: lab.id,
          title: lab.title,
          subtitle: lab.subtitle || '',
          description: lab.description || '',
          objectives: lab.objectives || '',
          themes: lab.themes || '',
          value: lab.value || '',
          partner_logos: lab.partner_logos || '',
          partner_description: lab.partner_description || '',
          pdf_path: lab.pdf_path || '',
          pdf_url: resolveStorageUrl(lab.pdf_url, 'speaker-images', lab.pdf_path),
          displayOrder: lab.display_order ?? lab.id ?? 999
        }))
      };
    } catch (error) {
      throw error;
    }
  },

  async updateSpeakersPageData(pageData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        speakers_header_title: pageData.speakersHeader?.title || 'Nos Intervenants',
        speakers_intro_text: pageData.speakersIntro?.text || '',
        speakers_section_title: pageData.speakersSection?.title || 'Intervenants Confirmés',
        online_speakers_header_title: pageData.onlineSpeakersHeader?.title || 'Intervenants En Ligne',
        online_speakers_intro_text: pageData.onlineSpeakersIntro?.text || '',
        online_speakers_section_title: pageData.onlineSpeakersSection?.title || 'Conférenciers Online',
        display_mode: pageData.displayMode || 'grouped',
        category_international_order: pageData.categoryInternationalOrder ?? 1,
        category_marocain_order: pageData.categoryMarocainOrder ?? 2,
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('speakers_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('speakers_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('speakers_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      throw error;
    }
  },

  async getLaboratories(editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) return [];

      const { data, error } = await supabase
        .from('speaker_laboratories')
        .select('*')
        .eq('edition_id', edition.id)
        .order('id', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw error;
    }
  },

  async getSpeakersForLabManagement(editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) return [];

      const { data: speakersData, error: speakersError } = await supabase
        .from('speakers')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      if (speakersError) throw speakersError;

      const { data: labsData } = await supabase
        .from('speaker_laboratories')
        .select('*')
        .eq('edition_id', edition.id)
        .order('id', { ascending: true });

      const labIds = (labsData || []).map(lab => lab.id);
      let labMembers = [];

      if (labIds.length > 0) {
        const { data, error } = await supabase
          .from('speaker_laboratory_members')
          .select('*')
          .in('laboratory_id', labIds);

        if (error) throw error;
        labMembers = data || [];
      }

      const labMap = new Map((labsData || []).map(lab => [lab.id, lab]));
      const roleLabels = {
        president: 'Président',
        international_expert: 'Expert international',
        academic_expert: 'Expert académique',
        security_expert: 'Expert sécurité'
      };

      return (speakersData || []).map(speaker => {
        const assignments = (labMembers || [])
          .filter(member => String(member.speaker_id) === String(speaker.id))
          .map(member => ({
            ...member,
            lab: labMap.get(member.laboratory_id) || null,
            roleLabel: roleLabels[member.role] || member.role
          }))
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

        const primaryAssignment = assignments[0] || null;

        return {
          ...speaker,
          labId: primaryAssignment?.laboratory_id || null,
          laboratoryAssignments: assignments,
          role: primaryAssignment?.role || null,
          roleLabel: primaryAssignment?.roleLabel || null,
          displayOrder: primaryAssignment?.display_order ?? speaker.display_order
        };
      });
    } catch (error) {
      throw error;
    }
  },

  async createLaboratory(labData, editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) throw new Error(`Edition ${editionYear} not found`);

      const insertData = {
        edition_id: edition.id,
        title: labData.title,
        subtitle: labData.subtitle || '',
        description: labData.description || '',
        objectives: labData.objectives || '',
        themes: labData.themes || '',
        value: labData.value || '',
        partner_logos: labData.partnerLogos || labData.partner_logos || '',
        partner_description: labData.partnerDescription || labData.partner_description || '',
        pdf_path: labData.pdfPath || labData.pdf_path || '',
        pdf_url: labData.pdfUrl || labData.pdf_url || ''
      };

      const { data, error } = await supabase
        .from('speaker_laboratories')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateLaboratory(labId, labData) {
    try {
      const { data, error } = await supabase
        .from('speaker_laboratories')
        .update({
          title: labData.title,
          subtitle: labData.subtitle || '',
          description: labData.description || '',
          objectives: labData.objectives || '',
          themes: labData.themes || '',
          value: labData.value || '',
          partner_logos: labData.partnerLogos || labData.partner_logos || '',
          partner_description: labData.partnerDescription || labData.partner_description || '',
          pdf_path: labData.pdfPath || labData.pdf_path || '',
          pdf_url: labData.pdfUrl || labData.pdf_url || ''
        })
        .eq('id', labId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteLaboratory(labId) {
    try {
      const { error } = await supabase
        .from('speaker_laboratories')
        .delete()
        .eq('id', labId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async assignSpeakerToLaboratory({ speakerId, laboratoryId, role, displayOrder }) {
    try {
      await supabase
        .from('speaker_laboratory_members')
        .delete()
        .eq('speaker_id', speakerId)
        .eq('laboratory_id', laboratoryId);

      const { data, error } = await supabase
        .from('speaker_laboratory_members')
        .insert([{
          speaker_id: speakerId,
          laboratory_id: laboratoryId,
          role,
          display_order: displayOrder || 0
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async assignSpeakersToLaboratory({ speakerIds, laboratoryId, role, displayOrder }) {
    try {
      if (!speakerIds?.length || !laboratoryId) return [];

      await supabase
        .from('speaker_laboratory_members')
        .delete()
        .in('speaker_id', speakerIds)
        .eq('laboratory_id', laboratoryId);

      const rows = speakerIds.map(speakerId => ({
        speaker_id: speakerId,
        laboratory_id: laboratoryId,
        role,
        display_order: displayOrder || 0
      }));

      const { data, error } = await supabase
        .from('speaker_laboratory_members')
        .insert(rows)
        .select();

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw error;
    }
  },

  async removeSpeakerFromLaboratory(speakerId, laboratoryId) {
    try {
      const { error } = await supabase
        .from('speaker_laboratory_members')
        .delete()
        .eq('speaker_id', speakerId)
        .eq('laboratory_id', laboratoryId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async addSpeaker(speakerData, editionYear, type = 'regular') {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const insertData = {
        edition_id: edition.id,
        person_id: speakerData.person_id || null,
        name: speakerData.name,
        title: speakerData.title,
        organization: speakerData.organization,
        image_path: speakerData.imagePath || '',
        image_url: speakerData.image,
        company_logo_path: speakerData.companyLogoPath || '',
        company_logo_url: speakerData.companyLogo,
        country: speakerData.country,
        flag_path: speakerData.flagPath || '',
        flag_url: speakerData.flagUrl,
        type: type,
        main_category: speakerData.mainCategory || 'international',
        display_order: speakerData.displayOrder || 0
      };

      const { data, error } = await supabase
        .from('speakers')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;

      // Link cadres if provided with ordering
      if (speakerData.cadreIds && speakerData.cadreIds.length > 0) {
        const links = speakerData.cadreIds.map((cadreId, index) => ({
          speaker_id: data.id,
          cadre_id: parseInt(cadreId),
          display_order: speakerData.cadreOrders ? speakerData.cadreOrders[index] || (index + 1) : (index + 1)
        }));
        await supabase.from('speaker_cadres').insert(links);
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateSpeaker(speakerId, speakerData) {
    try {
      const updateData = {
        name: speakerData.name,
        title: speakerData.title,
        organization: speakerData.organization,
        image_path: speakerData.imagePath || '',
        image_url: speakerData.image,
        company_logo_path: speakerData.companyLogoPath || '',
        company_logo_url: speakerData.companyLogo,
        country: speakerData.country,
        flag_path: speakerData.flagPath || '',
        flag_url: speakerData.flagUrl,
        main_category: speakerData.mainCategory,
        display_order: speakerData.displayOrder || 0,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('speakers')
        .update(updateData)
        .eq('id', speakerId)
        .select()
        .single();

      if (error) throw error;

      // Update cadre links with ordering
      if (speakerData.cadreIds !== undefined) {
        // Remove old links
        await supabase
          .from('speaker_cadres')
          .delete()
          .eq('speaker_id', speakerId);

        // Add new links with ordering
        if (speakerData.cadreIds.length > 0) {
          const links = speakerData.cadreIds.map((cadreId, index) => ({
            speaker_id: speakerId,
            cadre_id: parseInt(cadreId),
            display_order: speakerData.cadreOrders ? speakerData.cadreOrders[index] || (index + 1) : (index + 1)
          }));
          await supabase.from('speaker_cadres').insert(links);
        }
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateSpeakerOrder(speakerId, newOrder) {
    try {
      const { data, error } = await supabase
        .from('speakers')
        .update({ display_order: newOrder })
        .eq('id', speakerId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateSpeakerOrderInCadre(speakerId, cadreId, newOrder) {
    try {
      const { data, error } = await supabase
        .from('speaker_cadres')
        .update({ display_order: newOrder })
        .eq('speaker_id', speakerId)
        .eq('cadre_id', cadreId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteSpeaker(speakerId) {
    try {
      // Get speaker data first to delete files
      const { data: speaker } = await supabase
        .from('speakers')
        .select('image_path, company_logo_path')
        .eq('id', speakerId)
        .single();

      if (speaker) {
        // Delete files from storage
        if (speaker.image_path) {
          await this.deleteSpeakerFile(speaker.image_path);
        }
        if (speaker.company_logo_path) {
          await this.deleteSpeakerFile(speaker.company_logo_path);
        }
      }

      // Delete record from database
      const { error } = await supabase
        .from('speakers')
        .delete()
        .eq('id', speakerId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
};
